import { RangeHorizon, ResampleIntervals } from '@toggle/chart';
import { hasKeyValues } from '@toggle/helpers';
import { AssetSubClassCode, Entity, isEtf } from '@toggle/toggle';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { InsightsGroup } from '~/components/insights-group/InsightsGroup';
import { SectionTitle } from '~/components/section-title/SectionTitle';
import { useChartNavigate } from '~/hooks/use-chart-navigate/useChartNavigate';
import { useExplore } from '~/hooks/use-explore/useExplore';
import { useQueryInsights } from '~/hooks/use-insight/useInsight';
import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { About } from '~/modules/about/About';
import { OverviewFinancials } from '~/modules/overview-financials/OverviewFinancials';
import { OverviewKeyValues } from '~/modules/overview-key-values/OverviewKeyValues';
import { ThemeKnowledgeGraph } from '~/modules/overview-knowledge-graph/components/theme-knowledge-graph/ThemeKnowledgeGraph';
import { OverviewValuation } from '~/modules/overview-valuation-metrics/OverviewValuationMetrics';
import { SimpleChart } from '~/modules/simple-chart/SimpleChart';
import { ChartSearchParamsAssetValue } from '~/utils/chart-search-utils/chart-search-utils';
import { ChartState } from '~/views/turbo-chart/hooks/use-chart-state/useChartState';
import { ChartSettings } from '~/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { CompetitorsSection } from '~/widgets/knowledge-graph-widget/components/competitors-tab/tabs/competitor-detail/components/CompetitorsSection';
import { ThemeDrawer } from '~/widgets/theme-widget/theme-drawer/ThemeDrawer';

import * as S from './Overview.styles';

export interface OverviewProps {
  entity: Entity;
}

export const Overview = ({ entity }: OverviewProps) => {
  const { t } = useTranslation([
    'widget',
    'common',
    'chart',
    'analyze',
    'countries',
    'scenarioCommander',
    'screener',
    'scenario',
  ]);
  const { params } = useQueryParams();
  const { openDrawer } = useInsightDrawer();

  const chartNavigate = useChartNavigate();

  const {
    data,
    isError: isInsightsError,
    isLoading: isInsightsLoading,
  } = useExplore({
    entityTags: [entity.tag],
    filters: {
      entity_tag: {
        values: [
          {
            tags: [entity.tag],
          },
        ],
      },
      period: { values: [{ duration: 1, unit: 'week' }] },
    },
    sortBy: undefined,
    combined: true,
  });

  const filteredInsights = useMemo(() => {
    return data?.pages[0]?.entities[0]?.insights ?? [];
  }, [data]);

  const insights = useQueryInsights(filteredInsights.map(i => i.id));

  const insightsWithMetadata = useMemo(() => {
    return filteredInsights.map(insight => {
      const metadata = insights.data.find(
        data => data.id === insight.id
      )?.metadata;

      return {
        ...insight,
        metadata,
      };
    });
  }, [insights.data, filteredInsights]);

  const chartSettings: ChartSettings = {
    assets: [
      {
        tag: entity.tag,
        snake: entity.default_snake,
        paneIndex: 0,
      },
    ],
    resample: ResampleIntervals.OneHour,
    horizon: RangeHorizon.OneMonth,
    domain: null,
    hidden: undefined,
    paneSettings: {},
  };

  const isStock = entity.sub_class === AssetSubClassCode.EquityStock;
  const entityIsEtf = isEtf(entity);
  const displayAbout = isStock || entityIsEtf;

  const navigateToChart = (chartState: ChartState) => {
    const entityValue: ChartSearchParamsAssetValue = [
      entity.tag,
      entity.default_snake,
      0,
    ];

    chartNavigate([entityValue], {
      resample: chartState.resample,
      horizon: chartState.horizon,
      seriesType: chartState.chartPanes[0].seriesType,
    });
  };

  return (
    <>
      <ThemeDrawer entity={entity} />
      <S.OverviewRoot>
        <SimpleChart
          entity={entity}
          key={entity.tag}
          initialChartSettings={chartSettings}
          isLegacyVariant={false}
          onClickFullscreen={navigateToChart}
          maxHorizon={RangeHorizon.TenYears}
          eventsConfig={{
            TMI: true,
          }}
        />
        {hasKeyValues(entity.tag) && (
          <ErrorBoundaryWrapper>
            <OverviewKeyValues entityTag={entity.tag} />
          </ErrorBoundaryWrapper>
        )}
        <S.Flex>
          <S.SplitSection>
            {displayAbout && <About entity={entity} />}
          </S.SplitSection>
          {isStock && (
            <S.SplitSection>
              <SectionTitle
                title={t('widget:assetOverview.knowledgeGraph.name')}
              />
              <S.KnowledgeGraphWrapper>
                <ThemeKnowledgeGraph
                  entityTag={entity.tag}
                  isInDrawer={false}
                />
                <CompetitorsSection entityTag={entity.tag} />
              </S.KnowledgeGraphWrapper>
            </S.SplitSection>
          )}
        </S.Flex>
        {isStock && (
          <>
            <OverviewFinancials entity={entity} />
            <OverviewValuation entity={entity} />
          </>
        )}

        {!!insightsWithMetadata.length && (
          <S.Flex>
            <S.SplitSection>
              <InsightsGroup
                horizon={RangeHorizon.OneWeek}
                activeInsightId={params.activeInsight ?? ''}
                selectedInsights={insightsWithMetadata}
                isError={isInsightsError}
                isInsightsLoading={isInsightsLoading}
                handleSetActiveInsightId={insightId => {
                  openDrawer(insightId, { source: 'asset_overview' });
                }}
              />
            </S.SplitSection>
          </S.Flex>
        )}
      </S.OverviewRoot>
    </>
  );
};
